// Drop Zone v2 — app heuristics (spec #761, §B.2).
//
// A run manifest is the most specific intent: it means "this is meant to run."
// The decision tree checks these first, and the ambiguity rule (spec §B.2) says
// an app signal beats co-present inspiration files.

import type { DropEntry, DetectionSignal, AppFramework } from '../types.js';
import { extOf } from './inspiration.js';

/**
 * Manifest basenames that mark an artifact as an app, with the weight each
 * carries. Checked at the top level and one level deep (spec §B.2: "inspect
 * top level + one level deep"), e.g. a `cloudgrid.yaml` just inside a zipped
 * project folder.
 */
const MANIFEST_WEIGHTS: Readonly<Record<string, number>> = {
  'cloudgrid.yaml': 1.0,
  'cloudgrid.yml': 1.0,
  dockerfile: 0.9,
  'package.json': 0.85,
  'requirements.txt': 0.85,
  'pyproject.toml': 0.85,
  'go.mod': 0.9,
  'cargo.toml': 0.9,
};

/** Path depth = number of segments. `package.json` is 1, `app/package.json` is 2. */
function depthOf(path: string): number {
  return path.split('/').filter((seg) => seg.length > 0).length;
}

function basenameLower(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1).toLowerCase();
}

export interface AppFinding {
  /** True when at least one app manifest is present at depth ≤ 2. */
  isApp: boolean;
  framework: AppFramework | undefined;
  signals: DetectionSignal[];
  /** Set when a top-level/near-top `package.json` exists (for the Layer-2 deps read). */
  hasPackageJson: boolean;
}

/**
 * Weigh the app signals over a noise-filtered file list (filenames only — this
 * is the Layer-1 pass). The folder/Layer-2 pass refines the framework guess by
 * reading `package.json` contents afterward.
 */
export function findAppSignals(files: DropEntry[]): AppFinding {
  const signals: DetectionSignal[] = [];
  const present = new Set<string>();

  for (const f of files) {
    if (depthOf(f.path) > 2) continue;
    const base = basenameLower(f.path);
    if (base in MANIFEST_WEIGHTS) present.add(base);
  }

  const hasNextConfig = files.some((f) => {
    if (depthOf(f.path) > 2) return false;
    const base = basenameLower(f.path);
    return (
      base === 'next.config.js' ||
      base === 'next.config.mjs' ||
      base === 'next.config.ts' ||
      base === 'next.config.cjs'
    );
  });
  const hasPyEntrypoint = files.some((f) => {
    if (depthOf(f.path) > 2) return false;
    const base = basenameLower(f.path);
    return base === 'app.py' || base === 'main.py' || base === 'wsgi.py';
  });

  for (const base of present) {
    signals.push({
      marker: `has-${base.replace(/\./g, '-')}`,
      weight: MANIFEST_WEIGHTS[base] ?? 0.8,
      contributes_to: 'app',
    });
  }
  if (hasNextConfig) {
    signals.push({ marker: 'has-next-config', weight: 0.9, contributes_to: 'app' });
  }
  if (hasPyEntrypoint && (present.has('requirements.txt') || present.has('pyproject.toml'))) {
    signals.push({
      marker: 'has-python-entrypoint',
      weight: 0.6,
      contributes_to: 'app',
    });
  }

  const isApp = signals.length > 0;
  const framework = isApp
    ? deriveFramework(present, hasNextConfig, hasPyEntrypoint)
    : undefined;

  return { isApp, framework, signals, hasPackageJson: present.has('package.json') };
}

/**
 * Pick the most specific framework for the friendly one-liner. Order matters:
 * the strongest, most specific signal wins.
 */
function deriveFramework(
  present: Set<string>,
  hasNextConfig: boolean,
  hasPyEntrypoint: boolean,
): AppFramework {
  if (hasNextConfig) return 'nextjs';
  if (present.has('requirements.txt') || present.has('pyproject.toml') || hasPyEntrypoint) {
    return 'python';
  }
  if (present.has('go.mod')) return 'go';
  if (present.has('cargo.toml')) return 'rust';
  // package.json with no Next signal — generic node app; refined by Layer 2.
  if (present.has('package.json')) return 'other';
  if (present.has('cloudgrid.yaml') || present.has('cloudgrid.yml')) return 'cloudgrid-yaml';
  if (present.has('dockerfile')) return 'docker-only';
  return 'other';
}

/** Detect a Next.js dependency from already-parsed package.json contents (Layer 2). */
export function packageJsonHasNext(pkg: unknown): boolean {
  if (!pkg || typeof pkg !== 'object') return false;
  const p = pkg as { dependencies?: unknown; devDependencies?: unknown };
  const deps = p.dependencies;
  const dev = p.devDependencies;
  const inObj = (o: unknown): boolean =>
    !!o && typeof o === 'object' && 'next' in (o as Record<string, unknown>);
  return inObj(deps) || inObj(dev);
}

/** Exposed for the noise filter and tests. */
export { extOf };
